/**
 * Content Templates API Client
 * Routes: /api/workspaces/:workspaceId/content/templates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ContentType } from './content';

// =============================================================================
// Types
// =============================================================================

export interface FigmaSlot {
  slot_name: string;
  node_id: string;
  description: string;
}

export interface ContentTemplate {
  id: string;
  workspace_id: string | null;
  name: string;
  description: string | null;
  content_type: ContentType | null;
  figma_url: string | null;
  figma_file_key: string | null;
  figma_node_id: string | null;
  figma_node_name: string | null;
  figma_thumbnail_url: string | null;
  figma_slots: FigmaSlot[];
  document_id: string | null;
  is_default: boolean;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateContentTemplatePayload {
  name: string;
  description?: string;
  content_type?: ContentType | null;
  figma_url?: string;
  document_id?: string;
  is_default?: boolean;
  is_active?: boolean;
}

export interface UpdateContentTemplatePayload {
  name?: string;
  description?: string | null;
  content_type?: ContentType | null;
  figma_url?: string;
  figma_thumbnail_url?: string | null;
  figma_slots?: FigmaSlot[];
  figma_node_name?: string;
  document_id?: string | null;
  is_default?: boolean;
  is_active?: boolean;
}

// =============================================================================
// API Client
// =============================================================================

function baseUrl(workspaceId: string) {
  return `/api/workspaces/${workspaceId}/content/templates`;
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json();
}

export const contentTemplatesApi = {
  list(workspaceId: string, contentType?: ContentType): Promise<ContentTemplate[]> {
    const qs = contentType ? `?content_type=${contentType}` : '';
    return request<{ templates: ContentTemplate[] }>(`${baseUrl(workspaceId)}${qs}`)
      .then(d => d.templates);
  },

  get(workspaceId: string, id: string): Promise<ContentTemplate> {
    return request<ContentTemplate>(`${baseUrl(workspaceId)}/${id}`);
  },

  create(workspaceId: string, data: CreateContentTemplatePayload): Promise<ContentTemplate> {
    return request<ContentTemplate>(baseUrl(workspaceId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(workspaceId: string, id: string, data: UpdateContentTemplatePayload): Promise<ContentTemplate> {
    return request<ContentTemplate>(`${baseUrl(workspaceId)}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete(workspaceId: string, id: string): Promise<void> {
    return request<void>(`${baseUrl(workspaceId)}/${id}`, { method: 'DELETE' });
  },
};

// =============================================================================
// React Query Hooks
// =============================================================================

export function useContentTemplates(workspaceId: string | undefined, contentType?: ContentType) {
  return useQuery({
    queryKey: ['content-templates', workspaceId, contentType],
    queryFn: () => contentTemplatesApi.list(workspaceId!, contentType),
    enabled: !!workspaceId,
  });
}

export function useContentTemplate(workspaceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['content-template', workspaceId, id],
    queryFn: () => contentTemplatesApi.get(workspaceId!, id!),
    enabled: !!workspaceId && !!id,
  });
}

export function useCreateContentTemplate(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateContentTemplatePayload) =>
      contentTemplatesApi.create(workspaceId!, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content-templates', workspaceId] });
    },
  });
}

export function useUpdateContentTemplate(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContentTemplatePayload }) =>
      contentTemplatesApi.update(workspaceId!, id, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: ['content-templates', workspaceId] });
      qc.invalidateQueries({ queryKey: ['content-template', workspaceId, id] });
    },
  });
}

export function useDeleteContentTemplate(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contentTemplatesApi.delete(workspaceId!, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content-templates', workspaceId] });
    },
  });
}
